import React from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { 
  TrendingUp, 
  FileText, 
  DollarSign, 
  BarChart3,
  Activity
} from 'lucide-react'
import { PartnerReferralsStats } from '@/types/partner'
import { formatCurrency, formatCompactNumber } from '@/utils'

interface PartnerReferralsStatsProps {
  stats: PartnerReferralsStats | null
  loading?: boolean
  className?: string
}

const PartnerReferralsStatsComponent: React.FC<PartnerReferralsStatsProps> = ({ 
  stats, 
  loading = false, 
  className = '' 
}) => {
  if (loading) {
    return (
      <div className={`space-y-6 ${className}`}>
        {/* Main Stats Cards Loading */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Stats Loading */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-6 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Breakdown Cards Loading */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-6 w-32 mb-4" />
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-6 w-8" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <Skeleton className="h-6 w-28 mb-4" />
              <div className="space-y-3">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-8" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!stats) {
    return (
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">No stats available</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const statCards = [
    {
      title: 'Total Referrals',
      value: formatCompactNumber(stats.total_referrals),
      icon: <BarChart3 size={20} />,
      color: 'text-blue-600 dark:text-blue-500',
      bgColor: 'bg-blue-500/10'
    },
    {
      title: 'Total Inquiries',
      value: formatCompactNumber(stats.total_inquiries),
      icon: <FileText size={20} />,
      color: 'text-green-600 dark:text-green-500',
      bgColor: 'bg-green-500/10'
    },
    {
      title: 'Total RFQs',
      value: formatCompactNumber(stats.total_rfqs),
      icon: <Activity size={20} />,
      color: 'text-purple-600 dark:text-purple-500',
      bgColor: 'bg-purple-500/10'
    },
    {
      title: 'Total Tranche Size',
      value: formatCurrency(stats.total_tranche_size),
      icon: <DollarSign size={20} />,
      color: 'text-orange-600 dark:text-orange-500',
      bgColor: 'bg-orange-500/10'
    }
  ]

  const additionalStats = [
    {
      title: 'Total Valuation',
      value: formatCurrency(stats.total_valuation),
      icon: <TrendingUp size={16} />,
      color: 'text-emerald-600 dark:text-emerald-500'
    },
    {
      title: 'Avg Tranche Size',
      value: formatCurrency(stats.avg_tranche_size),
      icon: <DollarSign size={16} />,
      color: 'text-amber-600 dark:text-amber-500'
    },
    {
      title: 'Avg Valuation',
      value: formatCurrency(stats.avg_valuation),
      icon: <TrendingUp size={16} />,
      color: 'text-indigo-600 dark:text-indigo-500'
    }
  ]

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Main Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, index) => (
          <Card key={index} variant="elevated">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.bgColor}`}>
                  <span className={card.color}>
                    {card.icon}
                  </span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {card.title}
                </Badge>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {card.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {additionalStats.map((stat, index) => (
          <Card key={index} variant="default">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={stat.color}>
                  {stat.icon}
                </span>
                <span className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </span>
              </div>
              <div className="text-lg font-semibold text-foreground">
                {stat.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Status Breakdown */}
        <Card variant="default">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Status Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(stats.status_breakdown).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={status === 'approved' ? 'default' : 
                              status === 'pending' ? 'secondary' : 
                              status === 'rejected' ? 'destructive' : 'outline'}
                      className="capitalize"
                    >
                      {status}
                    </Badge>
                  </div>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Type Breakdown */}
        <Card variant="default">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Type Breakdown</h3>
            <div className="space-y-3">
              {Object.entries(stats.type_breakdown).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={type === 'buy' ? 'default' : 'secondary'}
                      className="capitalize"
                    >
                      {type}
                    </Badge>
                  </div>
                  <span className="font-semibold">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default PartnerReferralsStatsComponent
